import { Dialog, DialogType, IconButton, Stack } from '@fluentui/react'
import { ErrorCircleRegular, SquareRegular } from '@fluentui/react-icons'
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

import DOMPurify from 'dompurify'
import { isEmpty } from 'lodash'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import uuid from 'react-uuid'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { XSSAllowAttributes, XSSAllowTags } from '../../constants/sanatizeAllowables'
import styles from './Chat.module.css'

import { useBoolean } from '@fluentui/react-hooks'
import supersub from 'remark-supersub'
import {
  AzureSqlServerExecResults,
  ChatHistoryLoadingState,
  ChatMessage,
  ChatResponse,
  Citation,
  ConversationRequest,
  CosmosDBStatus,
  ErrorMessage,
  ExecResults,
  ToolMessageContent,
  historyGenerate,
  historyUpdate,
} from '../../api'
import { Answer } from '../../components/Answer'
import { ChatHistoryPanel } from '../../components/ChatHistory/ChatHistoryPanel'
import { QuestionInput } from '../../components/QuestionInput'
import { AppStateContext } from '../../state/AppProvider'
import { sampleQuestions, stripHtmlTags } from '../../components/SampleQuestions/SampleQuestions'
import { jwtDecode } from "jwt-decode";

interface UserPayload {
  given_name: string;
}

const enum messageStatus {
  NotRunning = 'Not Running',
  Processing = 'Processing',
  Done = 'Done',
}

const Chat = () => {
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const [activeCitation, setActiveCitation] = useState<Citation>()
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const abortFuncs = useRef([] as AbortController[])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [execResults, setExecResults] = useState<ExecResults[]>([])
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning)
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true)
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
  const [logo, setLogo] = useState('')
  const [answerId, setAnswerId] = useState<string>('')

  const [inputValue, setInputValue] = useState<string>('')
  interface UserPayload {
    given_name?: string;
  }

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: 'Close',
    subText: errorMsg?.subtitle,
  }

  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  }

  const [givenName, setGivenName] = useState<string>('');
  const [chatTitle, setChatTitle] = useState<string>('');
  const defaultChatTitle = "User";

  useEffect(() => {
    const maxAttempts = 10;
    let attempts = 0;
    const intervalId = setInterval(() => {
      const keys = Object.keys(sessionStorage);
      const targetKey = keys.find(key => key.endsWith("https://analysis.windows.net/powerbi/api/.default--"));

      if (targetKey) {
        const storedValue = sessionStorage.getItem(targetKey);

        if (storedValue) {
          try {
            const parsedValue = JSON.parse(storedValue);
            const secret = parsedValue.secret;

            const decoded = jwtDecode<UserPayload>(secret);
            setGivenName(decoded.given_name || '');

            setChatTitle((ui?.chat_title || defaultChatTitle).replace('{given_name}', decoded.given_name || defaultChatTitle));            clearInterval(intervalId);
          } catch (error) {
            console.error("Failed to parse JSON from session storage or decode JWT", error);
          }
        }
      }

      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [ui?.chat_title]);

  const [ASSISTANT, TOOL, ERROR] = ['assistant', 'tool', 'error']
  const NO_CONTENT_ERROR = 'No content in messages object.'
  useEffect(() => {
    if (
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working &&
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured &&
      appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
      hideErrorDialog
    ) {
      let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
      setErrorMsg({
        title: 'Chat history is not enabled',
        subtitle: subtitle,
      })
      toggleErrorDialog()
    }
  }, [appStateContext?.state.isCosmosDBAvailable])

  const handleErrorDialogClose = () => {
    toggleErrorDialog()
    setTimeout(() => {
      setErrorMsg(null)
    }, 500)
  }

  useEffect(() => {
    if (!appStateContext?.state.isLoading) {
      setLogo(ui?.chat_logo || ui?.logo || '')
    }
  }, [appStateContext?.state.isLoading])

  useEffect(() => {
    setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
  }, [appStateContext?.state.chatHistoryLoadingState])

  let assistantMessage = {} as ChatMessage
  let toolMessage = {} as ChatMessage
  let assistantContent = ''

  useEffect(() => parseExecResults(execResults), [execResults])

  const parseExecResults = (exec_results_: any): void => {
    if (exec_results_ == undefined) return
    const exec_results = exec_results_.length === 2 ? exec_results_ : exec_results_.splice(2)
    appStateContext?.dispatch({
      type: 'SET_ANSWER_EXEC_RESULT',
      payload: { answerId: answerId, exec_result: exec_results },
    })
  }

  const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
    if (typeof resultMessage.content === 'string' && resultMessage.content.includes('all_exec_results')) {
      const parsedExecResults = JSON.parse(resultMessage.content) as AzureSqlServerExecResults
      setExecResults(parsedExecResults.all_exec_results)
      assistantMessage.context = JSON.stringify({
        all_exec_results: parsedExecResults.all_exec_results,
      })
    }

    if (resultMessage.role === ASSISTANT) {
      setAnswerId(resultMessage.id)
      assistantContent += resultMessage.content
      assistantMessage = { ...assistantMessage, ...resultMessage }
      assistantMessage.content = assistantContent

      if (resultMessage.context) {
        toolMessage = {
          id: uuid(),
          role: TOOL,
          content: resultMessage.context,
          date: new Date().toISOString(),
          agent_name: resultMessage.agent_name,
        }
      }
    }

    if (resultMessage.role === TOOL) toolMessage = resultMessage

    if (!conversationId) {
      isEmpty(toolMessage)
        ? setMessages([...messages, userMessage, assistantMessage])
        : setMessages([...messages, userMessage, toolMessage, assistantMessage])
    } else {
      isEmpty(toolMessage)
        ? setMessages([...messages, assistantMessage])
        : setMessages([...messages, toolMessage, assistantMessage])
    }
  }

  const makeApiRequestWithCosmosDB = async (question: ChatMessage['content'], conversationId?: string) => {
    setIsLoading(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)
    const questionContent =
      typeof question === 'string'
        ? question
        : [
            { type: 'text', text: question[0].text },
            { type: 'image_url', image_url: { url: question[1].image_url.url } },
          ]
    question = typeof question !== 'string' && question[0]?.text?.length > 0 ? question[0].text : question

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: questionContent as string,
      date: new Date().toISOString(),
      agent_name: 'user',
    }

    let request: ConversationRequest
    let conversation
    if (conversationId) {
      conversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoading(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
        request = {
          messages: [...conversation.messages.filter(answer => answer.role !== ERROR)],
        }
      }
    } else {
      request = {
        messages: [userMessage].filter(answer => answer.role !== ERROR),
      }
      setMessages(request.messages)
    }
    let result = {} as ChatResponse
    var errorResponseMessage = 'Please try again. If the problem persists, please contact the site administrator.'
    try {
      setProcessMessages(messageStatus.Processing)
      const response = conversationId
        ? await historyGenerate(request, abortController.signal, conversationId)
        : await historyGenerate(request, abortController.signal)
      if (!response?.ok) {
        const responseJson = await response.json()
        errorResponseMessage =
          responseJson.error === undefined ? errorResponseMessage : parseErrorMessage(responseJson.error)
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: `There was an error generating a response. Chat history can't be saved at this time. ${errorResponseMessage}`,
          date: new Date().toISOString(),
          agent_name: 'error',
        }
        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation.messages.push(errorChatMsg)
        } else {
          setMessages([...messages, userMessage, errorChatMsg])
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        setMessages([...resultConversation.messages])
        return
      }
      if (response?.body) {
        const reader = response.body.getReader()

        let runningText = ''
        while (true) {
          setProcessMessages(messageStatus.Processing)
          const { done, value } = await reader.read()
          if (done) break

          var text = new TextDecoder('utf-8').decode(value)
          const objects = text.split('\n')
          objects.forEach(obj => {
            try {
              if (obj !== '' && obj !== '{}') {
                runningText += obj
                result = JSON.parse(runningText)
                if (!result.choices?.[0]?.messages?.[0].content) {
                  errorResponseMessage = NO_CONTENT_ERROR
                  throw Error()
                }
                if (result.choices?.length > 0) {
                  result.choices[0].messages.forEach(msg => {
                    msg.id = msg.id || result.id
                    msg.date = new Date().toISOString()
                  })
                  if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                    setShowLoadingMessage(false)
                  }
                  result.choices[0].messages.forEach(resultObj => {
                    processResultMessage(resultObj, userMessage, conversationId)
                  })
                }
                runningText = ''
              } else if (result.error) {
                throw Error(result.error)
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e)
                throw e
              } else {
                console.log('Incomplete message. Continuing...')
              }
            }
          })
        }

        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation.selected_agent = result.selected_agent
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage)
        } else {
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date,
            selected_agent: result.selected_agent,
          }
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage)
        }

        if (!resultConversation) {
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        isEmpty(toolMessage)
          ? setMessages([...messages, assistantMessage])
          : setMessages([...messages, toolMessage, assistantMessage])
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = `An error occurred. ${errorResponseMessage}`
        if (result.error?.message) {
          errorMessage = result.error.message
        } else if (typeof result.error === 'string') {
          errorMessage = result.error
        }

        errorMessage = parseErrorMessage(errorMessage)

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString(),
          agent_name: 'error',
        }
        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation.messages.push(errorChatMsg)
        } else {
          if (!result.history_metadata) {
            console.error('Error retrieving data.', result)
            let errorChatMsg: ChatMessage = {
              id: uuid(),
              role: ERROR,
              content: errorMessage,
              date: new Date().toISOString(),
              agent_name: 'error',
            }
            setMessages([...messages, userMessage, errorChatMsg])
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date,
            selected_agent: result.selected_agent,
          }
          resultConversation.messages.push(errorChatMsg)
        }
        if (!resultConversation) {
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        setMessages([...messages, errorChatMsg])
      } else {
        setMessages([...messages, userMessage])
      }
    } finally {
      setIsLoading(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
    }
    return abortController.abort()
  }

  const tryGetRaiPrettyError = (errorMessage: string) => {
    try {
      // Using a regex to extract the JSON part that contains "innererror"
      const match = errorMessage.match(/'innererror': ({.*})\}\}/)
      if (match) {
        // Replacing single quotes with double quotes and converting Python-like booleans to JSON booleans
        const fixedJson = match[1]
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
        const innerErrorJson = JSON.parse(fixedJson)
        let reason = ''
        // Check if jailbreak content filter is the reason of the error
        const jailbreak = innerErrorJson.content_filter_result.jailbreak
        if (jailbreak.filtered === true) {
          reason = 'Jailbreak'
        }

        // Returning the prettified error message
        if (reason !== '') {
          return (
            'The prompt was filtered due to triggering Azure OpenAI’s content filtering system.\n' +
            'Reason: This prompt contains content flagged as ' +
            reason +
            '\n\n' +
            'Please modify your prompt and retry. Learn more: https://go.microsoft.com/fwlink/?linkid=2198766'
          )
        }
      }
    } catch (e) {
      console.error('Failed to parse the error:', e)
    }
    return errorMessage
  }

  const parseErrorMessage = (errorMessage: string) => {
    let errorCodeMessage = errorMessage.substring(0, errorMessage.indexOf('-') + 1)
    const innerErrorCue = "{\\'error\\': {\\'message\\': "
    if (errorMessage.includes(innerErrorCue)) {
      try {
        let innerErrorString = errorMessage.substring(errorMessage.indexOf(innerErrorCue))
        if (innerErrorString.endsWith("'}}")) {
          innerErrorString = innerErrorString.substring(0, innerErrorString.length - 3)
        }
        innerErrorString = innerErrorString.replaceAll("\\'", "'")
        let newErrorMessage = errorCodeMessage + ' ' + innerErrorString
        errorMessage = newErrorMessage
      } catch (e) {
        console.error('Error parsing inner error message: ', e)
      }
    }

    return tryGetRaiPrettyError(errorMessage)
  }

  const stopGenerating = () => {
    abortFuncs.current.forEach(a => a.abort())
    setShowLoadingMessage(false)
    setIsLoading(false)
  }

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages)
    } else {
      setProcessMessages(messageStatus.Processing)
      setMessages([])
      setIsCitationPanelOpen(false)
      setIsIntentsPanelOpen(false)
      setActiveCitation(undefined)
      setProcessMessages(messageStatus.Done)
    }
  }, [appStateContext?.state.currentChat])

  useLayoutEffect(() => {
    const saveToDB = async (messages: ChatMessage[], id: string) => {
      const response = await historyUpdate(messages, id)
      return response
    }

    if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
      if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
        if (!appStateContext?.state.currentChat?.messages) {
          console.error('Failure fetching current chat state.')
          return
        }
        const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

        if (!noContentError) {
          saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
            .then(async res => {
              if (!res.ok) {
                let errorMessage =
                  "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator."
                let errorChatMsg: ChatMessage = {
                  id: uuid(),
                  role: ERROR,
                  content: errorMessage,
                  date: new Date().toISOString(),
                  agent_name: 'error',
                }
                if (!appStateContext?.state.currentChat?.messages) {
                  let err: Error = {
                    ...new Error(),
                    message: 'Failure fetching current chat state.',
                  }
                  throw err
                }
                setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
              }
              return res as Response
            })
            .catch(err => {
              console.error('Error: ', err)
              let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
              }
              return errRes
            })
        }
      } else {
      }
      appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat })
      setMessages(appStateContext.state.currentChat.messages)
      setProcessMessages(messageStatus.NotRunning)
    }
  }, [processMessages])

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showLoadingMessage, processMessages])

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation)
    setIsCitationPanelOpen(true)
  }

  const onShowExecResult = (answerId: string) => {
    setIsIntentsPanelOpen(true)
  }

  const onViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes('blob.core')) {
      window.open(citation.url, '_blank')
    }
  }

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent
        return toolMessage.citations
      } catch {
        return []
      }
    }
    return []
  }

  const parsePlotFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const execResults = JSON.parse(message.content) as AzureSqlServerExecResults
        const codeExecResult = execResults.all_exec_results.at(-1)?.code_exec_result

        if (codeExecResult === undefined) {
          return null
        }
        return codeExecResult.toString()
      } catch {
        return null
      }
      // const execResults = JSON.parse(message.content) as AzureSqlServerExecResults;
      // return execResults.all_exec_results.at(-1)?.code_exec_result;
    }
    return null
  }

  const components = {
    code({ node, ...props }: { node: any; [key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter
          style={nord}
          language={language}
          PreTag="div"
          {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    },
  }
  return (
    <div
      className={styles.container}
      role="main">
      <Stack
        horizontal
        className={styles.chatRoot}>
        <div className={styles.chatContainer}>
          {!messages || messages.length < 1 ? (
            <Stack className={styles.chatEmptyState}>
              <h1 className={styles.chatEmptyStateTitle}>{chatTitle}</h1>
              <h2 className={styles.chatEmptyStateSubtitle}>{ui?.chat_description}</h2>
              {ui?.chat_additional_info && (
                <h3 className={styles.chatEmptyStateAdditionalInfo}>{ui.chat_additional_info}</h3>
              )}
            </Stack>

          ) : (
            <div
              className={styles.chatMessageStream}
              style={{ marginBottom: isLoading ? '40px' : '0px' }}
              role="log">
              {messages.map((answer, index) => (
                <div key={answer.id}>
                  {answer.role === 'user' && answer.content.length > 0 ? (
                    <div
                      className={styles.chatMessageUser}
                      tabIndex={0}>
                      <div className={styles.chatMessageUserMessage}>
                        {typeof answer.content === 'string' && answer.content ? (
                          answer.content
                        ) : Array.isArray(answer.content) ? (
                          <>
                            {answer.content[0].text}{' '}
                            <img
                              className={styles.uploadedImageChat}
                              src={answer.content[1].image_url.url}
                              alt="Uploaded Preview"
                            />
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : answer.role === 'assistant' ? (
                    <div className={styles.chatMessageGpt}>
                      {typeof answer.content === 'string' && (
                        <Answer
                          answer={{
                            answer: answer.content,
                            citations: parseCitationFromMessage(messages[index - 1]),
                            generated_chart: parsePlotFromMessage(messages[index - 1]),
                            message_id: answer.id,
                            feedback: answer.feedback,
                            exec_results: execResults,
                            agent_name: answer.agent_name,
                          }}
                          onCitationClicked={c => onShowCitation(c)}
                          onExectResultClicked={() => onShowExecResult(answerId)}
                        />
                      )}
                    </div>
                  ) : answer.role === ERROR ? (
                    <div className={styles.chatMessageError}>
                      <Stack
                        horizontal
                        className={styles.chatMessageErrorContent}>
                        <ErrorCircleRegular
                          className={styles.errorIcon}
                          style={{ color: 'rgba(182, 52, 67, 1)' }}
                        />
                        <span>Error</span>
                      </Stack>
                      <span className={styles.chatMessageErrorContent}>
                        {typeof answer.content === 'string' && answer.content}
                      </span>
                    </div>
                  ) : null}
                </div>
              ))}
              {showLoadingMessage && (
                <div key="loading">
                  <div className={styles.chatMessageGpt}>
                    <Answer
                      answer={{
                        answer: 'Generating answer...',
                        citations: [],
                        generated_chart: null,
                        agent_name: '',
                      }}
                      onCitationClicked={() => null}
                      onExectResultClicked={() => null}
                    />
                  </div>
                </div>
              )}
              <div ref={chatMessageStreamEnd} />
            </div>
          )}

          {messages.length === 0 && (
            <div className={styles.sampleQuestionsButtonsContainer} aria-label="Sample questions">
              {sampleQuestions.map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={styles.sampleQuestionButton}
                  onClick={() => {
                    const plain = stripHtmlTags(q);
                    setInputValue(plain);
                    const convId = appStateContext?.state.currentChat?.id ? appStateContext.state.currentChat.id : undefined;
                    makeApiRequestWithCosmosDB(plain, convId);
                    setInputValue('');
                  }}
                  aria-label={`Use sample question: ${stripHtmlTags(q)}`}
                  dangerouslySetInnerHTML={{ __html: q }}
                />
              ))}
            </div>
          )}
          <Stack className={styles.chatInputContainer}>
            {isLoading && messages.length > 0 && (
              <Stack
                className={styles.stopGeneratingContainer}
                role="button"
                aria-label="Stop generating"
                tabIndex={0}
                onClick={stopGenerating}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGenerating() : null)}>
                <SquareRegular
                  className={styles.stopGeneratingIcon}
                  aria-hidden="true"
                />
                <span
                  className={styles.stopGeneratingText}
                  aria-hidden="true">
                  Stop generating
                </span>
              </Stack>
            )}
            <Stack className={styles.chatInputInnerContainer}>
              <QuestionInput
                clearOnSend
                disabled={isLoading}
                placeholder="Type a new question..."
                conversationId={
                  appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined
                }
                value={inputValue}
                onChange={setInputValue}
                onSend={(question, id) => {
                  makeApiRequestWithCosmosDB(question, id)
                }}
              />
            </Stack>
          </Stack>
          <Dialog
            hidden={hideErrorDialog}
            onDismiss={handleErrorDialogClose}
            dialogContentProps={errorDialogContentProps}
            modalProps={modalProps}></Dialog>
        </div>
        {/* Citation Panel */}
        {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
          <Stack.Item
            className={styles.citationPanel}
            tabIndex={0}
            role="tabpanel"
            aria-label="Citations Panel">
            <Stack
              aria-label="Citations Panel Header Container"
              horizontal
              className={styles.citationPanelHeaderContainer}
              horizontalAlign="space-between"
              verticalAlign="center">
              <span
                aria-label="Citations"
                className={styles.citationPanelHeader}>
                Citations
              </span>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                aria-label="Close citations panel"
                onClick={() => setIsCitationPanelOpen(false)}
              />
            </Stack>
            <h5
              className={styles.citationPanelTitle}
              tabIndex={0}
              title={
                activeCitation.url && !activeCitation.url.includes('blob.core')
                  ? activeCitation.url
                  : activeCitation.title ?? ''
              }
              onClick={() => onViewSource(activeCitation)}>
              {activeCitation.title}
            </h5>
            <div tabIndex={0}>
              <ReactMarkdown
                linkTarget="_blank"
                className={styles.citationPanelContent}
                children={activeCitation.content}
                remarkPlugins={[remarkGfm, supersub]}
                rehypePlugins={[rehypeRaw]}
                components={components}
              />
            </div>
          </Stack.Item>
        )}
        {messages && messages.length > 0 && isIntentsPanelOpen && (
          <Stack.Item
            className={styles.citationPanel}
            tabIndex={0}
            role="tabpanel"
            aria-label="Intents Panel">
            <Stack
              aria-label="Intents Panel Header Container"
              horizontal
              className={styles.citationPanelHeaderContainer}
              horizontalAlign="space-between"
              verticalAlign="center">
              <span
                aria-label="Intents"
                className={styles.citationPanelHeader}>
                Intents
              </span>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                aria-label="Close intents panel"
                onClick={() => setIsIntentsPanelOpen(false)}
              />
            </Stack>
            <Stack horizontalAlign="space-between">
              {appStateContext?.state?.answerExecResult[answerId]?.map((execResult: ExecResults, index) => (
                <Stack
                  className={styles.exectResultList}
                  verticalAlign="space-between">
                  <>
                    <span>Intent:</span> <p>{execResult.intent}</p>
                  </>
                  {execResult.search_query && (
                    <>
                      <span>Search Query:</span>
                      <SyntaxHighlighter
                        style={nord}
                        wrapLines={true}
                        lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                        language="sql"
                        PreTag="p">
                        {execResult.search_query}
                      </SyntaxHighlighter>
                    </>
                  )}
                  {execResult.search_result && (
                    <>
                      <span>Search Result:</span> <p>{execResult.search_result}</p>
                    </>
                  )}
                  {execResult.code_generated && (
                    <>
                      <span>Code Generated:</span>
                      <SyntaxHighlighter
                        style={nord}
                        wrapLines={true}
                        lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                        language="python"
                        PreTag="p">
                        {execResult.code_generated}
                      </SyntaxHighlighter>
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
          </Stack.Item>
        )}
        {appStateContext?.state.isChatHistoryOpen &&
          appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && <ChatHistoryPanel />}
      </Stack>
    </div>
  )
}

export default Chat
